import { useMemo } from 'react';
import { GenericThresholdOptions, StepOptions, ThresholdColorPalette } from '../';

export interface FindThresholdParams<T extends StepOptions> {
  thresholds: GenericThresholdOptions<T>;
  /** Palette used to fill in colors if no color is on the step. */
  palette?: ThresholdColorPalette;
  /** Upper bound of the value range; used to evaluate percent-mode steps as a percentage of the range. */
  max?: number;
}

// Its a common scenario that the panels need to figure out which threshold is active
// so that it can pass appropriate color/message information to the visualization component,
// which doesn't necessarily concern itself with thresholds.
export function createFindThresholdFn<T extends StepOptions>({ thresholds, palette, max }: FindThresholdParams<T>) {
  // Make sure the steps are sorted
  const steps = [...(thresholds.steps || [])];
  steps.sort((a, b) => a.value - b.value);
  // We are going to potentitially do mutation on this base, so we copy thresholds.base instead of just using that
  const base = { ...(thresholds.base || {}) } as NonNullable<GenericThresholdOptions<T>['base']>;
  const isPercentMode = thresholds.mode === 'Percent';

  if (!base.color && thresholds.default_color) {
    base.color = thresholds.default_color;
  } else if (!base.color && palette) {
    base.color = palette.defaultColor;
  }

  // In percent mode the step values are percentages (0-100) and need to be compared against the
  // value's position within the [0, max] range. When a positive max is provided, scale the value
  // to that range; otherwise fall back to assuming the input is already a 0-1 fractional percent.
  // The `max > 0` guard avoids division-by-zero producing Infinity/NaN, which would otherwise pin
  // every value to the highest threshold bucket.
  const percentScaleDivisor = isPercentMode && max !== undefined && max > 0 ? max : undefined;

  return (uncorrectedValue: number) => {
    let value: number;
    if (percentScaleDivisor !== undefined) {
      value = (uncorrectedValue / percentScaleDivisor) * 100;
    } else if (isPercentMode) {
      value = uncorrectedValue * 100;
    } else {
      value = uncorrectedValue;
    }

    // If there are no steps, then we always use base
    if (steps.length === 0) {
      return base;
    }

    // This will find the index of the step after where the value falls
    const index = steps.findIndex((step) => step.value > value);

    // If index is -1 then there was no step whose value was greater than this value
    // This means it falls into the final step
    if (index === -1) {
      const step = steps[steps.length - 1];

      if (!step) {
        return base;
      } else if (step.color === undefined && palette !== undefined) {
        return { color: palette.palette[steps.length - 1], ...step };
      } else {
        return step;
      }
    }
    // if index is 0, then that means the value is below all the steps, so we return base
    else if (index === 0) {
      return base;
    } else {
      const step = steps[index - 1];

      if (!step) {
        return base;
      } else if (step.color === undefined && palette !== undefined) {
        return { color: palette.palette[index - 1], ...step };
      } else {
        return step;
      }
    }
  };
}

// This hook will return a utility function for looking up which threshold is the active
export function useFindThreshold<T extends StepOptions>({ thresholds, palette, max }: FindThresholdParams<T>) {
  return useMemo(() => {
    return createFindThresholdFn({ thresholds, palette, max });
  }, [thresholds, palette, max]);
}

// Variant to use in non react scenarios
export function findThreshold<T extends StepOptions>({
  thresholds,
  value,
  palette,
  max,
}: FindThresholdParams<T> & { value: number }) {
  const findThresholdFn = createFindThresholdFn({ thresholds, palette, max });
  return findThresholdFn(value);
}

/**
 * Converts a percentage threshold value into an absolute value based on the maximum value.
 */
export function convertToAbsoluteValue(value: number, maxValue: number): number {
  return (value / 100) * maxValue;
}

/**
 * Calculates the threshold step value based whether the mode is Absolute or Percent.
 */
export function calculateThresholdStepValue(
  step: StepOptions,
  thresholdMode: GenericThresholdOptions['mode'],
  maxValue?: number
): number {
  if (thresholdMode === 'Percent' && maxValue !== undefined) {
    return convertToAbsoluteValue(step.value, maxValue);
  }
  return step.value;
}
